<template>
  <div class="feedback-modal-overlay" @click="handleBackdropClick">
    <!-- Blurred background -->
    <div class="feedback-backdrop"></div>

    <!-- Modal content -->
    <div class="feedback-modal" @click.stop>
      <!-- Title -->
      <div class="feedback-title">
        <h2 v-if="isArabic">كيف تقيم رضاك عن النظام اليوم؟</h2>
        <h2 v-else>Wie zufrieden sind Sie heute mit dem System?</h2>
      </div>

      <!-- Rating Scale -->
      <div class="rating-container">
        <div class="rating-labels">
          <span class="label-bad" v-if="isArabic">سيء جداً</span>
          <span class="label-bad" v-else>Sehr schlecht</span>
          <span class="label-good" v-if="isArabic">ممتاز</span>
          <span class="label-good" v-else>Sehr gut</span>
        </div>

        <div class="rating-buttons">
          <button
            v-for="rating in 10"
            :key="rating"
            @click="selectRating(rating)"
            :class="['rating-btn', { 'selected': selectedRating === rating }]"
          >
            {{ rating }}
          </button>
        </div>
      </div>

      <!-- Action Buttons -->
      <div class="action-buttons">
        <button
          @click="submitFeedback"
          :disabled="selectedRating === null || isSubmitting"
          class="btn-submit"
        >
          <span v-if="isArabic">إرسال</span>
          <span v-else>Senden</span>
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import { mapState } from 'vuex';

export default {
  name: 'FeedbackModal',
  data() {
    return {
      selectedRating: null,
      isSubmitting: false
    };
  },
  computed: {
    ...mapState(['language', 'selectedRestaurantId', 'firebase']),

    isArabic() {
      return this.language === 'ar';
    }
  },
  methods: {
    selectRating(rating) {
      this.selectedRating = rating;
    },

    async submitFeedback() {
      if (this.selectedRating === null || this.isSubmitting) return;

      this.isSubmitting = true;

      try {
        const { ref, set } = await import('firebase/database');
        const db = this.firebase.database();

        const timestamp = new Date().toISOString();
        const feedbackRef = ref(db, `restaurants/${this.selectedRestaurantId}/feedback/${Date.now()}`);

        await set(feedbackRef, {
          rating: this.selectedRating,
          timestamp: timestamp,
          date: new Date().toLocaleDateString('de-DE'),
          time: new Date().toLocaleTimeString('de-DE')
        });

        // Mark as submitted today
        localStorage.setItem('feedbackSubmittedDate', new Date().toDateString());

        this.$emit('close');
      } catch (error) {
        console.error('Error submitting feedback:', error);
        alert(this.isArabic ? 'خطأ في إرسال التقييم' : 'Fehler beim Senden der Bewertung');
      } finally {
        this.isSubmitting = false;
      }
    },

    handleBackdropClick() {
      // Shake modal to indicate it must be interacted with
      const modal = this.$el.querySelector('.feedback-modal');
      if (modal) {
        modal.classList.add('shake');
        setTimeout(() => modal.classList.remove('shake'), 500);
      }
    }
  }
};
</script>

<style scoped>
.feedback-modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
}

.feedback-backdrop {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.7);
  backdrop-filter: none;
}

.feedback-modal {
  position: relative;
  background: linear-gradient(135deg, var(--ods-surface) 0%, var(--ods-surface) 100%);
  border-radius: var(--ods-radius-lg);
  padding: 40px;
  max-width: 600px;
  width: 90%;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.5);
  border: 2px solid rgba(59, 130, 246, 0.3);
}

.feedback-title {
  text-align: center;
  margin-bottom: 30px;
}

.feedback-title h2 {
  font-size: 24px;
  font-weight: 700;
  color: var(--ods-accent-ink);
  font-family: inherit;
}

.rating-container {
  margin-bottom: 30px;
}

.rating-labels {
  display: flex;
  justify-content: space-between;
  margin-bottom: 15px;
  font-size: 14px;
  font-weight: 600;
}

.label-bad {
  color: #ef4444;
}

.label-good {
  color: #22c55e;
}

.rating-buttons {
  display: grid;
  grid-template-columns: repeat(10, 1fr);
  gap: 8px;
}

.rating-btn {
  aspect-ratio: 1;
  border: 2px solid rgba(59, 130, 246, 0.3);
  background: rgba(30, 41, 59, 0.5);
  color: var(--ods-ink-2);
  border-radius: var(--ods-radius);
  font-size: 18px;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.3s ease;
}

.rating-btn:hover {
  background: rgba(59, 130, 246, 0.2);
  border-color: var(--ods-accent);
  transform: scale(1.1);
}

.rating-btn.selected {
  background: linear-gradient(135deg, var(--ods-accent), var(--ods-accent));
  border-color: var(--ods-accent);
  color: var(--ods-ink);
  transform: scale(1.15);
  box-shadow: 0 8px 20px rgba(59, 130, 246, 0.4);
}

.action-buttons {
  display: flex;
  gap: 15px;
  justify-content: center;
}

.btn-submit {
  padding: 12px 40px;
  border-radius: var(--ods-radius);
  font-size: 16px;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.3s ease;
  border: none;
  font-family: inherit;
  background: linear-gradient(135deg, #22c55e, #16a34a);
  color: var(--ods-ink);
}

.btn-submit:hover:not(:disabled) {
  transform: translateY(-2px);
  box-shadow: 0 8px 20px rgba(34, 197, 94, 0.4);
}

.btn-submit:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

@keyframes shake {
  0%, 100% { transform: translateX(0); }
  25% { transform: translateX(-10px); }
  75% { transform: translateX(10px); }
}

.feedback-modal.shake {
  animation: shake 0.3s ease-in-out;
}

@media (max-width: 640px) {
  .rating-buttons {
    grid-template-columns: repeat(5, 1fr);
    gap: 10px;
  }

  .rating-btn {
    font-size: 16px;
  }

  .feedback-title h2 {
    font-size: 20px;
  }
}
</style>
